/**
 * One way to write money, data and dates across the dashboard.
 *
 * The 2026-09-29 walk-through found three money styles side by side
 * ("$12.58 USD", "−0.84 USD", "14.40 USDT"), dates as ISO in one tab,
 * DD.MM.YYYY in another and a full locale timestamp in a third, and usage
 * written as "14300 MB".
 *
 * Money is "$" everywhere: a balance is dollar value, 1:1 with the
 * stablecoin that paid for it. Which coin moved is the payment rail, and the
 * rail is named only where it matters — the top-up checkout note and the
 * referral payout ("Paid in USDT", network, wallet).
 */
import { getLocale } from '../i18n';
import { INTL_LOCALE } from '../i18n/types';

const intlTag = (): string => INTL_LOCALE[getLocale()] ?? INTL_LOCALE.en;

/** "$12.58"; negatives "−$0.84"; `signed` adds "+" to credits too. */
export function fmtMoney(micro: number, opts: { signed?: boolean } = {}): string {
  const body = `$${(Math.abs(micro) / 1_000_000).toFixed(2)}`;
  if (micro < 0) return `−${body}`;
  return opts.signed ? `+${body}` : body;
}

/**
 * "860 MB", "3.4 GB", "140 GB". GB is 1024 MB — the same factor the $/GB
 * prices use, so a card's usage and the price list agree.
 */
export function fmtData(mb: number): string {
  if (!Number.isFinite(mb) || mb <= 0) return '0 MB';
  if (mb < 10) return `${mb.toFixed(1)} MB`;
  if (mb < 1024) return `${Math.round(mb)} MB`;
  const gb = mb / 1024;
  return `${gb < 100 ? gb.toFixed(1) : Math.round(gb)} GB`;
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Sep 29" in the active language; the year only when it is not this year. */
export function fmtDate(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return '—';
  const withYear = d.getFullYear() !== new Date().getFullYear();
  return new Intl.DateTimeFormat(intlTag(), withYear
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short' }).format(d);
}

/** fmtDate plus the time, for events where the hour matters. */
export function fmtDateTime(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return '—';
  const withYear = d.getFullYear() !== new Date().getFullYear();
  return new Intl.DateTimeFormat(intlTag(), {
    day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' as const } : {}),
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}
